import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, Params } from '@angular/router';

import { MagnetService } from '@mirage-projects/cw-commons';
import { CommonsConfigEnvironmentRoutes } from '@mirage-projects/cw-models';

import { EnvironmentConfigurationService } from '../services/environment-configuration.service';

/**
 * Guard that redirects to an external url if the path has an assignated external url in the environment configuration
 *
 * A "routing" property in env-configuration is check everty time a routing is being done. Something like:
 *      "routing": { embedRedirect: "<embedUrl>", routes: [{"path": "panel", "url": "v2/viewer"}, {...}, ...]}
 *
 * When the redirect component will encounter a path that matches the current router location, it will replace the url content into the current url route. Eg:
 * We can have here three different cases:
 *   Replace the relative part of a route: We have http://localhost:4200/panel, for the configuration above, the redirector piece (the service that does the changes when it sees a route change), will return the url http://localhost:4200/v2/panel
 *   Replace the current router with a new entire one: We are in http://dashboard, for the config above, it will go to https://alpha.pointcloudviz.com/v2/dashboard
 *   Replace the route with parameters: If we are in http://alpha.pointcloudviz.com/viewer/1234, we'll go to https://alpha.pointcloudviz.com/v2/viewer/:1234
 *   If the embedRedirect property is configured, it will redirect to this URL when the application is executed in embed mode.
 */
@Injectable({
    providedIn: 'root',
})
export class HybridRoutingGuard {
    constructor(
        private envConfigService: EnvironmentConfigurationService,
        private magnetService: MagnetService,
        private ngZone: NgZone
    ) {}

    canActivate(route: ActivatedRouteSnapshot): boolean {
        if (this.magnetService.isEmbed()) {
            const embedRedirect = this.envConfigService.getEmbedRedirect();
            if (!embedRedirect) {
                return true;
            }

            const urlParams = new URLSearchParams(window.location.search);
            window.location.href = `${embedRedirect}?${urlParams.toString()}`;
            return false;
        }

        const path: string = route.routeConfig?.path ?? '';
        const externalUrl: string = this.getPathExternalUrl(path);

        const resultingPath = this.updateUrlWithParams(externalUrl, route.params);

        if (resultingPath) {
            this.ngZone.runTask(() => {
                window.location.replace(resultingPath);
            });

            return false;
        }

        return true;
    }

    private getPathExternalUrl(path: string): string {
        const routes: CommonsConfigEnvironmentRoutes[] | undefined = this.envConfigService.getExternalRoutes();

        if (!routes) {
            return '';
        }

        return routes.find((route) => route.path === path)?.url ?? '';
    }

    private updateUrlWithParams(path: string, params: Params): string {
        const [finalPath, paramKey] = path.split('/:');

        if (paramKey && params) {
            const paramValue = params[paramKey];
            return `${finalPath}/${paramValue}`;
        } else {
            return path;
        }
    }
}
